import { useQuery } from '@apollo/client/react';
import {
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ListErrorState } from '../../../../components/ListErrorState.js';
import { useDeniedTooltip } from '../../../../lib/access/index.js';
import { assertUuidRouteParams } from '../../../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../../../modules/iam/permissions-context.js';
import { ImportLeafLinkOrderDialog } from '../../../../modules/leaflink-companies/import-leaflink-order-dialog.js';
import { LeafLinkOrderFilterRow } from '../../../../modules/leaflink-companies/leaflink-order-filter-row.js';
import { makeLeafLinkOrderColumns } from '../../../../modules/leaflink-companies/leaflink-order-list-columns.js';
import { LeafLinkWebUrlCompanyContext } from '../../../../modules/leaflink-companies/leaflink-web-url-company-context.js';
import {
  GET_LEAFLINK_ORDERS,
  type GetLeafLinkOrdersQueryResult,
  type GetLeafLinkOrdersQueryVariables,
  type LeafLinkOrderListItem,
} from '../../../../modules/leaflink-companies/operations.js';
import { toUsableLeafLinkCompanyBrands } from '../../../../modules/leaflink-companies/to-usable-leaflink-company-brands.js';
import { useLeafLinkCompanyQuery } from '../../../../modules/leaflink-companies/use-leaflink-company-query.js';
import { useLeafLinkOrderFilters } from '../../../../modules/leaflink-companies/use-leaflink-order-filters.js';

const ORDERS_PAGE_SIZE = 25;

export const Route = createFileRoute('/integrations/leaflink/$leafLinkIntegrationId/orders')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: LeafLinkOrdersPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function LeafLinkOrdersPage() {
  const { leafLinkIntegrationId } = Route.useParams();
  const { permissions } = usePermissions();
  const deniedTooltip = useDeniedTooltip();
  const canImport =
    permissions.includes('leafLinkIntegration:edit') && permissions.includes('salesOrder:edit');
  const importDeniedReason = canImport
    ? null
    : deniedTooltip('leafLinkIntegration:edit', 'salesOrder:edit');
  const filters = useLeafLinkOrderFilters();
  const { offset, setOffset } = filters;
  const [importingOrder, setImportingOrder] = useState<LeafLinkOrderListItem | null>(null);

  const {
    data: companyData,
    error: companyError,
    refetch: refetchCompany,
  } = useLeafLinkCompanyQuery(leafLinkIntegrationId);
  const company = companyData?.leafLinkCompany ?? null;

  const filterableBrands = useMemo(
    () => toUsableLeafLinkCompanyBrands(company?.leafLinkCompanyBrand ?? []),
    [company],
  );

  // Resolves the order's remote brand to the Nudool brand it's paired with, the same match the
  // import mutation makes server-side -- scopes the import dialog's customer picker instead of
  // handing it the whole customer catalog.
  const importingOrderBrandId = filterableBrands.find(
    (brand) => brand.leafLinkBrandId === importingOrder?.brand,
  )?.id;

  const { data, loading, error, refetch } = useQuery<
    GetLeafLinkOrdersQueryResult,
    GetLeafLinkOrdersQueryVariables
  >(GET_LEAFLINK_ORDERS, {
    variables: {
      leafLinkIntegrationId,
      limit: ORDERS_PAGE_SIZE,
      offset,
      ...filters.queryFilters,
    },
    skip: filters.createdRangeError !== undefined,
  });

  const orders = data?.leafLinkOrders.results ?? [];
  const count = data?.leafLinkOrders.count ?? 0;
  const hasNextPage = offset + ORDERS_PAGE_SIZE < count;
  const hasPreviousPage = offset > 0;

  const columns = useMemo(
    () => makeLeafLinkOrderColumns({ importDeniedReason, onImport: setImportingOrder }),
    [importDeniedReason],
  );

  return (
    <LeafLinkWebUrlCompanyContext.Provider value={company}>
      <DimeCard borderless className="gap-0 py-0">
        <DimeCardHeader className="px-0 mb-4">
          <DimeCardTitle className="text-base flex items-center gap-2">
            {company ? `${company.name} — LeafLink Orders` : 'LeafLink Orders'}
          </DimeCardTitle>
          <DimeCardDescription className="text-sm">
            Browse orders on LeafLink for this company.
          </DimeCardDescription>
        </DimeCardHeader>
        <LeafLinkOrderFilterRow filters={filters} brands={filterableBrands} />
        {companyError ? (
          <ListErrorState
            title="Could not load this LeafLink company"
            message={companyError.message}
            onRetry={() => void refetchCompany()}
          />
        ) : error ? (
          <ListErrorState
            title="Could not load LeafLink orders"
            message={error.message}
            onRetry={() => void refetch()}
          />
        ) : (
          <DimeDataTable
            columns={columns}
            data={orders}
            isLoading={loading}
            loadingRowCount={ORDERS_PAGE_SIZE}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={() => setOffset(offset + ORDERS_PAGE_SIZE)}
            onPreviousPage={() => setOffset(Math.max(0, offset - ORDERS_PAGE_SIZE))}
            showExport={false}
            emptyMessage={
              filters.createdRangeError
                ? 'Fix the created date range to see orders.'
                : 'No LeafLink orders found.'
            }
          />
        )}
        <ImportLeafLinkOrderDialog
          open={importingOrder !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setImportingOrder(null);
          }}
          leafLinkIntegrationId={leafLinkIntegrationId}
          order={importingOrder}
          brandId={importingOrderBrandId}
          onImported={() => {
            void refetch();
          }}
        />
      </DimeCard>
    </LeafLinkWebUrlCompanyContext.Provider>
  );
}
